import { useEffect, useState } from "react";
import { MessageCircle, Send, ShieldCheck, Info, Zap, Clock, Users, Sparkles, ExternalLink, ListChecks, Pause, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function WhatsAppPage() {
  const { user } = useAuth();
  const isAgent = user?.role === "travel_agent";
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [recordId, setRecordId] = useState("");
  const [records, setRecords] = useState([]);
  const [sending, setSending] = useState(false);

  const [bulkMode, setBulkMode] = useState(""); // "", "customers", "numbers"
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkLinks, setBulkLinks] = useState([]);
  const [aiBrief, setAiBrief] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  // paste-numbers bulk runner
  const [numbersText, setNumbersText] = useState("");
  const [queue, setQueue] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [pauseUntil, setPauseUntil] = useState(0);
  const [nowTs, setNowTs] = useState(Date.now());
  const [batch, setBatch] = useState({ size: 50, pause_minutes: 5 });

  const loadLog = () => api.get("/whatsapp/log").then((r) => setLog(r.data.messages)).catch(() => {});

  useEffect(() => {
    api.get("/whatsapp/config").then((r) => {
      setCfg(r.data);
      setBatch(r.data.batch || { size: 50, pause_minutes: 5 });
      const first = r.data.templates?.[0];
      if (first) { setTemplateId(first.id); setMessage(first.body || ""); }
    }).catch(() => {});
    loadLog();
    const linkType = isAgent ? "quotation" : "booking";
    api.get(`/records?type=${linkType}`).then((r) => setRecords(r.data.records)).catch(() => {});
    if (isAgent) api.get("/records?type=customer").then((r) => setCustomers(r.data.records)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);

  const canConnect = cfg?.can_connect;
  const canBulk = cfg?.can_bulk;
  const paused = pauseUntil > nowTs;
  const pauseLeft = paused ? Math.ceil((pauseUntil - nowTs) / 1000) : 0;

  const onTemplate = (id) => {
    setTemplateId(id);
    const t = cfg?.templates?.find((x) => x.id === id);
    setMessage(t?.body || "");
  };

  const compose = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/compose", {
        template_id: templateId, text: message, recipient_phone: phone, recipient_name: name, record_id: recordId && recordId !== "none" ? recordId : null,
      });
      window.open(data.wa_url, "_blank", "noopener");
      toast.success("Opening WhatsApp with your message ready to send");
      loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const toggleCust = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const aiDraft = async () => {
    if (aiBrief.trim().length < 4) { toast.error("Add a short brief first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/draft", { kind: "whatsapp", brief: aiBrief, recipient_name: name });
      if (data.draft?.message) { setMessage(data.draft.message); toast.success("AI draft ready — review and send"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const bulkGenerate = async () => {
    if (selected.length === 0) { toast.error("Select at least one customer."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/compose/bulk", { template_id: templateId, text: message, customer_ids: selected });
      setBulkLinks(data.messages);
      toast.success(`${data.messages.length} messages prepared`);
      loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const prepareNumbers = async () => {
    const nums = numbersText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (nums.length === 0) { toast.error("Paste at least one phone number with country code."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/compose/numbers", { template_id: templateId, text: message, numbers: nums, recipient_name: name });
      setQueue(data.messages); setBatch(data.batch || batch); setQIndex(0); setSentCount(0); setPauseUntil(0);
      toast.success(`${data.count} numbers ready — open and send one at a time`);
      loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const openNext = () => {
    if (qIndex >= queue.length || paused) return;
    window.open(queue[qIndex].wa_url, "_blank", "noopener");
    const nextSent = sentCount + 1;
    setSentCount(nextSent);
    setQIndex(qIndex + 1);
    if (nextSent % (batch.size || 50) === 0 && qIndex + 1 < queue.length) {
      setPauseUntil(Date.now() + (batch.pause_minutes || 5) * 60000);
      toast.info(`Pausing ${batch.pause_minutes} min after ${batch.size} messages to protect your number.`);
    }
  };

  const linkLabel = isAgent ? "Attach a quotation (adds price + share link)" : "Attach a booking (fills details)";
  const recordFieldName = records.find((r) => r.record_id === recordId)?.title;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="WhatsApp" icon={MessageCircle}
        subtitle="Compose updates from your bookings, quotes and templates, then send them from your own WhatsApp." />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <span data-testid="wa-level-badge" className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${!cfg ? "border-slate-200 bg-slate-50 text-slate-400" : canBulk ? "border-purple-200 bg-purple-50 text-purple-600" : canConnect ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
          {!cfg ? <ShieldCheck className="h-3.5 w-3.5 animate-pulse" /> : canBulk ? <Zap className="h-3.5 w-3.5" /> : canConnect ? <ShieldCheck className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
          {!cfg ? "Loading plan…" : `${canBulk ? "Bulk marketing" : canConnect ? "Connect & compose" : "Not on a WhatsApp plan"} · ${cfg.plan_name || "Free"}`}
        </span>
        {canBulk && (
          <>
            <button data-testid="wa-bulk-numbers-toggle" onClick={() => { setBulkMode((m) => m === "numbers" ? "" : "numbers"); setBulkLinks([]); }}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${bulkMode === "numbers" ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600 hover:border-purple-300"}`}>
              <ListChecks className="h-3.5 w-3.5" /> Paste numbers
            </button>
            {isAgent && (
              <button data-testid="wa-bulk-toggle" onClick={() => { setBulkMode((m) => m === "customers" ? "" : "customers"); setQueue([]); }}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${bulkMode === "customers" ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600 hover:border-purple-300"}`}>
                <Users className="h-3.5 w-3.5" /> From customers
              </button>
            )}
          </>
        )}
      </div>

      {cfg && !canConnect && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="wa-connect-locked">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <span><b>WhatsApp sending is a plan feature.</b> Upgrade to a WhatsApp-enabled plan to connect your number and send messages. You can still preview templates below.</span>
        </div>
      )}

      <SectionCard>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Template</Label>
            <Select value={templateId} onValueChange={onTemplate}>
              <SelectTrigger className="mt-1" data-testid="wa-template-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
              <SelectContent>{cfg?.templates?.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {!bulkMode && records.length > 0 && (
            <div>
              <Label className="text-xs text-slate-500">{linkLabel}</Label>
              <Select value={recordId} onValueChange={setRecordId}>
                <SelectTrigger className="mt-1" data-testid="wa-record-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {records.map((r) => <SelectItem key={r.record_id} value={r.record_id}>{r.title}</SelectItem>)}
                </SelectContent>
              </Select>
              {recordFieldName && <p className="mt-1 text-[11px] text-emerald-600">Details from “{recordFieldName}” will fill the placeholders.</p>}
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-500">Message {"{name}"} {"{title}"} {"{amount}"} {"{link}"} are auto-filled</Label>
            <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="wa-message" className="mt-1"
              placeholder="Write your message or pick a template above..." />
          </div>

          {canBulk && (
            <div className="rounded-xl border border-purple-100 bg-purple-50/60 p-3">
              <Label className="flex items-center gap-1.5 text-xs font-semibold text-purple-700"><Sparkles className="h-3.5 w-3.5" /> Draft with AI</Label>
              <Textarea value={aiBrief} onChange={(e) => setAiBrief(e.target.value)} rows={2} data-testid="wa-ai-brief" className="mt-1 bg-white"
                placeholder="e.g. follow up on the Bali enquiry, ask if they'd like to confirm" />
              <button type="button" onClick={aiDraft} disabled={aiLoading} data-testid="wa-ai-draft-btn"
                className="mt-2 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
                <Sparkles className="h-3.5 w-3.5" /> {aiLoading ? "Drafting..." : "Generate message"}
              </button>
              <p className="mt-1.5 text-[11px] text-purple-400">AI writes a friendly draft — it never invents prices or dates.</p>
            </div>
          )}

          {bulkMode === "numbers" ? (
            <div data-testid="wa-numbers-panel">
              <Label className="text-xs text-slate-500">Paste phone numbers (with country code, separated by commas, spaces or new lines)</Label>
              <Textarea rows={4} value={numbersText} onChange={(e) => setNumbersText(e.target.value)} data-testid="wa-numbers-input" className="mt-1"
                placeholder="919876543210, 919812345678&#10;441234567890" />
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-[11px] text-amber-700">
                <Pause className="mt-0.5 h-3.5 w-3.5 shrink-0" /> To protect your number, we pause for <b>{batch.pause_minutes} minutes</b> after every <b>{batch.size}</b> messages. You open and tap send on each one yourself — nothing is auto-sent.
              </div>
              <button onClick={prepareNumbers} disabled={sending} data-testid="wa-numbers-prepare"
                className="mt-3 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                <ListChecks className="h-4 w-4" /> {sending ? "Preparing..." : "Prepare list"}
              </button>

              {queue.length > 0 && (
                <div className="mt-4 rounded-xl border border-slate-100 p-3" data-testid="wa-numbers-runner">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">Sent {sentCount} / {queue.length}</span>
                    {qIndex < queue.length && <span className="text-xs text-slate-400">Next: +{queue[qIndex].phone}</span>}
                  </div>
                  <div className="mb-3 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full brand-gradient-bg transition-all" style={{ width: `${Math.round((sentCount / queue.length) * 100)}%` }} />
                  </div>
                  {qIndex >= queue.length ? (
                    <p className="text-sm font-medium text-emerald-600" data-testid="wa-numbers-done">✓ All {queue.length} messages opened. Reset the list to start a new batch.</p>
                  ) : paused ? (
                    <div className="flex items-center gap-2 rounded-lg bg-amber-50 p-2.5 text-sm text-amber-700" data-testid="wa-numbers-paused">
                      <Pause className="h-4 w-4" /> Cooling down — {Math.floor(pauseLeft / 60)}m {pauseLeft % 60}s left before the next {batch.size}.
                    </div>
                  ) : (
                    <button onClick={openNext} data-testid="wa-numbers-next"
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600">
                      <Send className="h-4 w-4" /> Open next in WhatsApp
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : bulkMode === "customers" ? (
            <div>
              <Label className="text-xs text-slate-500">Select customers ({selected.length} selected)</Label>
              <div className="mt-1 max-h-52 space-y-1 overflow-y-auto rounded-xl border border-slate-100 p-2" data-testid="wa-customer-list">
                {customers.length === 0 && <p className="p-2 text-xs text-slate-400">No customers yet. Add customers to send in bulk.</p>}
                {customers.map((c) => (
                  <label key={c.record_id} data-testid={`wa-customer-${c.record_id}`} className="flex cursor-pointer items-center gap-2 rounded-lg p-2 hover:bg-slate-50">
                    <input type="checkbox" checked={selected.includes(c.record_id)} onChange={() => toggleCust(c.record_id)} />
                    <span className="text-sm text-slate-700">{c.title}</span>
                    <span className="text-xs text-slate-400">{c.data?.phone || "no phone"}</span>
                  </label>
                ))}
              </div>
              <button onClick={bulkGenerate} disabled={sending} data-testid="wa-bulk-generate"
                className="mt-3 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                <Sparkles className="h-4 w-4" /> {sending ? "Preparing..." : "Prepare messages"}
              </button>
              {bulkLinks.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="wa-bulk-links">
                  {bulkLinks.map((m) => (
                    <div key={`${m.name}-${m.phone}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 p-2.5">
                      <div className="min-w-0"><p className="truncate text-sm font-medium text-slate-700">{m.name}</p><p className="text-xs text-slate-400">{m.has_phone ? m.phone : "no phone — pick chat"}</p></div>
                      <a href={m.wa_url} target="_blank" rel="noreferrer noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600"><ExternalLink className="h-3 w-3" /> Open</a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><Label className="text-xs text-slate-500">Recipient name (optional)</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="wa-recipient-name" className="mt-1" placeholder="e.g. Priya" /></div>
                <div><Label className="text-xs text-slate-500">Phone with country code (optional)</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="wa-recipient-phone" className="mt-1" placeholder="e.g. 919876543210" /></div>
              </div>
              <button onClick={compose} disabled={sending || (cfg && !canConnect)} data-testid="wa-open-btn"
                className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
                {cfg && !canConnect ? <Lock className="h-4 w-4" /> : <Send className="h-4 w-4" />} {sending ? "Preparing..." : cfg && !canConnect ? "Locked — upgrade to send" : "Open in WhatsApp"}
              </button>
              <p className="text-[11px] text-slate-400">Leave the phone blank to pick a chat inside WhatsApp. The message opens ready — you tap send.</p>
            </>
          )}
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="whatsapp-compliance-warning">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span><b>You are responsible for how you use WhatsApp.</b> Bulk/marketing messaging without recipient opt-in can get your number banned or restricted by WhatsApp. Don't spam. Suffle is not responsible for any ban, restriction, or action taken on your account. Suffle only opens WhatsApp with your message pre-filled — you always tap send yourself — and never fabricates delivery status.</span>
        </div>
      </SectionCard>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <SectionCard title="WhatsApp Connect">
          <ul className="space-y-2 text-sm text-slate-600">
            {["Connect & send from your own WhatsApp", "Share bookings, quotes & documents", "Single-message compose with templates"].map((t) => <li key={t} className="flex items-center gap-2"><ShieldCheck className={`h-4 w-4 ${canConnect ? "text-emerald-500" : "text-slate-300"}`} /> {t}</li>)}
          </ul>
          {!canConnect && <p className="mt-3 text-xs text-slate-400">Unlocked with a WhatsApp-enabled plan.</p>}
        </SectionCard>
        <SectionCard title="Bulk WhatsApp (Marketing)">
          <ul className="space-y-2 text-sm text-slate-600">
            {["Paste numbers & send in paced batches", "50 messages then a 5-min cooldown", "Bulk compose to a customer list", "AI-assisted message drafting"].map((t) => <li key={t} className="flex items-center gap-2"><Zap className={`h-4 w-4 ${canBulk ? "text-purple-500" : "text-slate-300"}`} /> {t}</li>)}
          </ul>
          {!canBulk && <p className="mt-3 text-xs text-slate-400">Unlocked with a Bulk / Marketing plan.</p>}
        </SectionCard>
      </div>

      <SectionCard title="Message Log" className="mt-5" testid="wa-log">
        <div className="mb-2 flex items-center gap-2 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> Messages you've composed (not delivery-confirmed)</div>
        {log.length === 0 ? (
          <p className="py-2 text-sm text-slate-400">Nothing composed yet.</p>
        ) : (
          <div className="divide-y divide-slate-50">
            {log.slice(0, 20).map((m) => (
              <div key={m.message_id} className="py-2.5" data-testid="wa-log-item">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-700">{m.recipient_name || m.recipient_phone || "Chat"}</p>
                  <span className="text-[11px] text-slate-400">{new Date(m.created_at).toLocaleString("en-GB")}</span>
                </div>
                <p className="truncate text-xs text-slate-500">{m.text}</p>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
